import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "@/firebase";

type FirebaseStorageUrlCache = {
  getUrl: (firebaseUrl: string) => Promise<string>;
};

const FirebaseStorageUrlContext = createContext<FirebaseStorageUrlCache>(
  {} as FirebaseStorageUrlCache,
);

export const FirebaseStorageUrlProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const urlCache = useRef(new Map<string, Promise<string>>());

  const getUrl = useCallback(async (firebaseUrl: string): Promise<string> => {
    // If URL starts with /, treat it as an absolute path relative to page URL
    if (firebaseUrl.startsWith("/")) {
      return firebaseUrl;
    }

    const cached = urlCache.current.get(firebaseUrl);
    if (cached) {
      return cached;
    }

    const request = getDownloadURL(ref(storage, firebaseUrl));

    urlCache.current.set(firebaseUrl, request);

    return request;
  }, []);

  const value = {
    getUrl,
  };

  return (
    <FirebaseStorageUrlContext.Provider value={value}>
      {children}
    </FirebaseStorageUrlContext.Provider>
  );
};

// Hook to get a Firebase Storage download URL
export const useFirebaseStorageUrl = (
  firebaseUrl: string | null | undefined,
) => {
  const { getUrl } = useContext(FirebaseStorageUrlContext);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!firebaseUrl) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const loadUrl = async () => {
      try {
        setLoading(true);
        setError(null);
        const downloadUrl = await getUrl(firebaseUrl);
        if (!cancelled) {
          setUrl(downloadUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
          setUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUrl();

    return () => {
      cancelled = true;
    };
  }, [firebaseUrl, getUrl]);

  return { url, loading, error };
};
